import actions from './actions'
import api from 'lib/api/secondStepApi'
import { formatError } from 'utils/formatError'
import { isV4Routed } from 'lib/api/ssapi/secondStepApi.router'
import { operations as ssapiOperations } from 'store/ssapiManager'
import { adaptSitesV4ToExpiredLicensesView } from 'lib/api/adapters/sites'
import { resolveIsExpired } from 'utils/licenseHelpers'

const fetchValidateActivationCode = activationCode => {
  return async dispatch => {
    dispatch(actions.fetchingValidateActivationCode())
    try {
      let response
      if (activationCode?.startsWith('FAM')) {
        response = await api.getValidateAccessCode(activationCode)
      } else {
        response = await api.getValidatePak(activationCode)
      }
      dispatch(actions.fetchingValidateActivationCodeSuccess(response.data))
    } catch (error) {
      console.error(error)
      dispatch(actions.fetchingValidateActivationCodeError(formatError(error)))
    }
  }
}

const fetchSetupLicensesForUser = () => {
  return async (dispatch, getState) => {
    dispatch(actions.fetchingSetupLicenses())

    try {
      const userEmail = getState()?.userProfileManager?.profile?.email
      const response = await api.getSetupLicensesForUser()
      const { data } = response

      const licenseData = data.reduce(
        (acc, license) => {
          const isOwnLicense = license.adminEmail === userEmail
          const isActiveLicense =
            license.isActive &&
            !resolveIsExpired({
              status: license.status,
              expirationDate: license.expirationDate,
            })

          if (!isOwnLicense || !isActiveLicense) return acc

          if (license.site === null) {
            acc.unassignedLicenses.push(license)
          } else {
            acc.adminLicenses.push(license)
          }
          return acc
        },
        { unassignedLicenses: [], adminLicenses: [] },
      )

      dispatch(
        actions.fetchingSetupLicensesSuccess(
          _filterLicensesWithoutProducts(licenseData),
        ),
      )
      return true
    } catch (error) {
      console.error(error)
      dispatch(actions.fetchingSetupLicensesError(formatError(error)))
      return false
    }
  }
}

const fetchActiveDigitalLicensesForUser = () => {
  return async (dispatch, getState) => {
    // v4 routing checks
    const v4AssetsRouted = isV4Routed('getNetSuiteAssetsForUser')
    const v4SitesRouted = isV4Routed('getUserSites')
    const useV4 = v4AssetsRouted || v4SitesRouted

    if (useV4) {
      // Maintain legacy loading flags for components still reading licenseManager
      dispatch(actions.fetchingActiveDigitalLicensesForUser())

      try {
        await Promise.all([
          dispatch(ssapiOperations.fetchUserNetSuiteAssets()),
          dispatch(ssapiOperations.fetchUserSites()),
        ])
        // Read freshly fetched v4 data from ssapiManager. Build the expired
        // licenses view directly from v4 status/expiration using the adapters
        // helper so legacy notification logic in the reducer receives expected
        // payload shape while components migrate to v4 selectors.
        const state = getState()
        const v4SitesRaw = state.ssapiManager?.sites
        const expiredLicenses = adaptSitesV4ToExpiredLicensesView(v4SitesRaw)

        // Do not hydrate legacy sites; legacy consumers behind flags rely on selectors switching to v4
        dispatch(actions.fetchingUserSitesSuccess(null))

        // Dispatch through the dedicated active digital licenses success path so notifications logic runs
        dispatch(
          actions.fetchingActiveDigitalLicensesForUserSuccess({
            expiredLicenses,
          }),
        )
      } catch (error) {
        console.error(error)
        dispatch(
          actions.fetchingActiveDigitalLicensesForUserError(formatError(error)),
        )
      }
      return
    }

    dispatch(actions.fetchingActiveDigitalLicensesForUser())

    try {
      const [{ data: licenseData }, { data: siteData }] = await Promise.all([
        api.getUserLicenses(),
        api.getUserSites(), // NOTE: extra call to get site.claims for expired license notifications - b/c some pages are already behind feature flags
      ])
      dispatch(
        actions.fetchingUserLicensesSuccess(
          _filterLicensesWithoutProducts(licenseData),
        ),
      )
      dispatch(actions.fetchingUserSitesSuccess(siteData))
    } catch (error) {
      console.error(error)
      dispatch(
        actions.fetchingActiveDigitalLicensesForUserError(formatError(error)),
      )
    }
  }
}

const fetchUserLicenses = () => {
  return async dispatch => {
    dispatch(actions.fetchingUserLicenses())

    try {
      const { data } = await api.getUserLicenses()
      dispatch(actions.fetchingUserLicensesSuccess(data))
    } catch (error) {
      console.error(error)
      dispatch(actions.fetchingUserLicensesError(formatError(error)))
    }
  }
}

const fetchUserSites = () => {
  return async (dispatch, getState) => {
    // v4 routing checks
    const v4SitesRouted = isV4Routed('getUserSites')
    if (v4SitesRouted) {
      dispatch(actions.fetchingUserSites())
      try {
        await dispatch(ssapiOperations.fetchUserSites())
        // Set legacy sites to null; consumers use v4 via selectors when routed
        dispatch(actions.fetchingUserSitesSuccess(null)) // TODO: check if this is correct
      } catch (error) {
        console.error(error)
        dispatch(actions.fetchingUserSitesError(formatError(error)))
      }
      return
    }

    dispatch(actions.fetchingUserSites())

    try {
      const { data } = await api.getUserSites()
      dispatch(actions.fetchingUserSitesSuccess(data))
    } catch (error) {
      console.error(error)
      dispatch(actions.fetchingUserSitesError(formatError(error)))
    }
  }
}

const resetState = () => {
  return async dispatch => {
    dispatch(actions.resetState())
  }
}

const sendActivationCode = activationCode => {
  return async dispatch => {
    dispatch(actions.sendingActivationCode())

    try {
      if (!activationCode) throw new Error('Failed to send Activation Code')
      let response
      if (activationCode.startsWith('FAM')) {
        response = await api.postAccessCode(activationCode)
      } else {
        response = await api.postPakNumber(activationCode)
      }
      dispatch(
        actions.sendingActivationCodeSuccess({
          response: response.data,
          activationCode,
        }),
      )
      // TODO: Waiting Planet Express profile refresh function - Add Here
    } catch (error) {
      console.error(error)
      dispatch(actions.sendingActivationCodeError(formatError(error)))
    }
  }
}

const setNotificationAsRead = notificationName => {
  return async dispatch => {
    try {
      dispatch(actions.setNotificationAsReadSuccess(notificationName))
    } catch (error) {
      console.error(error)
      dispatch(actions.setNotificationAsReadError(formatError(error)))
    }
  }
}

const _filterLicensesWithoutProducts = licenseData => {
  if (!licenseData) return licenseData
  // If v4 netsuiteassets/me shape (array), filter directly and return array
  if (Array.isArray(licenseData)) {
    return licenseData.filter(item => item && item.product != null)
  }
  // Otherwise, assume v3 shape with sections and filter arrays only
  const result = {}
  for (const licSection in licenseData) {
    const section = licenseData[licSection]
    result[licSection] = Array.isArray(section)
      ? section.filter(license => license && license.product != null)
      : section
  }
  return result
}

export default {
  fetchValidateActivationCode,
  fetchActiveDigitalLicensesForUser,
  fetchSetupLicensesForUser,
  fetchUserLicenses,
  fetchUserSites,
  resetState,
  sendActivationCode,
  setNotificationAsRead,
}
